import { Fragment, useState } from "react";
import type { LoteBanco } from "../types/reviewItem";
import { formatFecha, formatMonto } from "../utils/formato";
import { CLASE_CRUCE, ETIQUETA_CRUCE, descripcionCruce, resumenCruce } from "../utils/pagosBanco";

/**
 * Lotes de pago importados del banco, con cada pago y su cruce.
 * [NUEVO 03/11/2026]
 *
 * Es la vista del lado del BANCO: muestra también los pagos que no se pudieron
 * atribuir a una factura de la bandeja (proveedor no identificado, factura que
 * no está), que por definición no aparecen en la tabla de facturas.
 */
export function LotesBancoPanel({
  lotes,
  onAbrirPdf,
  onQuitar,
  quitandoId = null,
  quitarDeshabilitado = false,
  mostrarPagadoSinAprobacion = false,
}: {
  lotes: LoteBanco[];
  /** Solo para Contaduría: el PDF trae las cuentas completas. Sin esto, no hay botón. */
  onAbrirPdf?: (loteId: number) => void;
  /** Solo para Contaduría [NUEVO 04/11/2026]. Sin esto, no hay botón. La
   *  confirmación la hace quien lo pasa. */
  onQuitar?: (lote: LoteBanco) => void;
  /** Id del lote que se está quitando, para deshabilitar su botón. */
  quitandoId?: number | null;
  /** Mientras se importa o se quita otro lote. */
  quitarDeshabilitado?: boolean;
  /**
   * [NUEVO 04/11/2026] Solo con la funcionalidad `lotes_pago`: marca en rojo
   * los lotes que el banco pagó sin que se aprobaran en la app.
   */
  mostrarPagadoSinAprobacion?: boolean;
}) {
  const [abiertos, setAbiertos] = useState<Set<number>>(new Set());

  function alternar(id: number) {
    setAbiertos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  if (lotes.length === 0) {
    return <p className="sin-resolver">No hay lotes del banco para mostrar.</p>;
  }

  return (
    <table className="compact lotes-banco">
      <thead>
        <tr>
          <th>Fecha</th>
          <th>Moneda</th>
          <th className="celda-monto">Importe total</th>
          <th className="celda-monto">Pagos</th>
          <th>Estado en el banco</th>
          <th>Cruce</th>
          <th>Firmas</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {lotes.map((lote) => (
          <Fragment key={lote.id}>
            <tr>
              <td className="mono">{formatFecha(lote.fecha)}</td>
              <td>{lote.moneda}</td>
              <td className="mono celda-monto">{formatMonto(lote.importe_total)}</td>
              <td className="mono celda-monto">{lote.cantidad_pagos}</td>
              <td title={`ID de transacción ${lote.id_transaccion}`}>
                {lote.estado ?? "—"}
                {mostrarPagadoSinAprobacion && Boolean(lote.pagado_sin_aprobacion) && (
                  <span
                    className="badge badge-rej badge-pagado-sin-aprobacion"
                    title="El banco pagó este lote sin que se aprobara en la app"
                  >
                    Pagado sin aprobación en la app
                  </span>
                )}
              </td>
              <td>
                {resumenCruce(lote).map(({ estado, cantidad }) => (
                  <span key={estado} className={`badge ${CLASE_CRUCE[estado]} badge-cruce`} title={descripcionCruce(estado)}>
                    {cantidad} {ETIQUETA_CRUCE[estado].toLowerCase()}
                  </span>
                ))}
              </td>
              <td className="wrap-ok sin-resolver">
                {lote.firmas.map((f) => `${f.nombre} (${f.fecha})`).join(" · ") || "—"}
              </td>
              <td className="actions-cell">
                {onAbrirPdf && (
                  <button className="btn-ghost" onClick={() => onAbrirPdf(lote.id)}>
                    PDF
                  </button>
                )}
                <button className="btn-ghost" aria-expanded={abiertos.has(lote.id)} onClick={() => alternar(lote.id)}>
                  {abiertos.has(lote.id) ? "Ocultar pagos" : "Ver pagos"}
                </button>
                {onQuitar && (
                  <button
                    className="btn-ghost btn-quitar-lote"
                    disabled={quitarDeshabilitado}
                    onClick={() => onQuitar(lote)}
                  >
                    {quitandoId === lote.id ? "Quitando..." : "Quitar"}
                  </button>
                )}
              </td>
            </tr>
            {abiertos.has(lote.id) && (
              <tr className="detalle-row">
                <td colSpan={8}>
                  <table className="compact">
                    <thead>
                      <tr>
                        <th>Beneficiario</th>
                        <th>Banco / cuenta</th>
                        <th className="celda-monto">Importe</th>
                        <th>Referencia</th>
                        <th>Proveedor SAP</th>
                        <th>Cruce</th>
                        <th>Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lote.lineas.map((l) => (
                        <tr key={l.id}>
                          <td className="wrap-ok">{l.nombre}</td>
                          <td className="mono">
                            {l.banco ?? "—"} {l.cuenta}
                          </td>
                          <td className="mono celda-monto">{formatMonto(l.importe)}</td>
                          <td className="mono wrap-ok">{l.referencia ?? "—"}</td>
                          <td className="mono" title={`Identificado por: ${l.metodo_proveedor}`}>
                            {l.card_code ?? "—"}
                          </td>
                          <td>
                            <span className={`badge ${CLASE_CRUCE[l.estado_cruce]}`} title={descripcionCruce(l.estado_cruce)}>
                              {ETIQUETA_CRUCE[l.estado_cruce]}
                            </span>
                          </td>
                          <td className="wrap-ok sin-resolver">
                            {[
                              l.facturas.length > 0 && `Facturas: ${l.facturas.map((f) => f.nro ?? "?").join(", ")}`,
                              l.diferencia !== null && l.estado_cruce === "DIFERENCIA" && `Diferencia: ${formatMonto(l.diferencia)}`,
                              l.nros_no_encontrados && `No están en la bandeja: ${l.nros_no_encontrados}`,
                              l.nros_ambiguos && `Ambiguas: ${l.nros_ambiguos}`,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
